/* eslint-disable security/detect-object-injection */
import {makeAutoObservable} from 'mobx';

/**
 * The `NavigationService` class manages the application's navigation state using MobX for reactive state management.
 * This service provides a centralized way to track and update the current section of the application.
 * It utilizes MobX's `makeAutoObservable` to automatically make all properties and methods observable for reactive updates.
 */
export class NavigationService {
    /**
     * The currently active section identifier in the application navigation.
     * This property tracks which section of the application is currently being viewed or selected.
     * The value can be undefined when no specific section is active or during initial application state.
     */
    currentSection: string | undefined = undefined;

    /**
     * Creates a new instance of the NavigationService and initializes MobX observability.
     * The constructor automatically makes all class properties and methods observable using MobX.
     * Auto-binding ensures that all methods maintain their correct context when called.
     */
    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * Updates the currently active section in the application navigation state.
     * This method sets the current section identifier and triggers reactive updates through MobX observability.
     * The section parameter can be undefined to indicate that no specific section is currently active.
     *
     * @param section The section identifier to set as current, or undefined to clear the current section.
     *
     * @example
     * ```tsx
     * navigationService.setCurrentSection('dashboard');
     * navigationService.setCurrentSection('profile');
     * navigationService.setCurrentSection(undefined); // Clear current section
     * ```
     */
    setCurrentSection(section: string | undefined) {
        this.currentSection = section;
    }
}

export const navigationService = new NavigationService();